<template>
  <BaseModule
    v-bind="$props"
    class="banner-wrapper"
    data-module="banner"
    :style="{ '--max-height': useMaxHeight ? `${maxHeight}px` : 'unset' }"
    :class="{ 'banner-wrapper--proportion': activeBanners.length }"
  >
    <!-- classe 'seo-banner' apenas serve para o seletor de H1 no SEO -->
    <BannerCard
      v-if="useFixedText"
      v-bind="fixedBanner"
      class="seo-banner"
    >
      <slot></slot>
    </BannerCard>

    <Warning
      v-if="activeBanners.length === 0 && editing"
      text="O módulo não possui nenhum banner adicionado"
    >
    </Warning>

    <!-- Slider main container -->
    <div
      :key="swiperAutoplaySpeed"
      ref="banners-container"
      class="swiper"
    >
      <!-- Additional required wrapper -->
      <div class="swiper-wrapper">
        <!-- Slides -->
        <!-- classe 'seo-banner' apenas serve para o seletor de H1 no SEO -->
        <BannerCard
          v-for="(banner, index) in activeBanners"
          :key="`${banner.id}-${useFixedText}`"
          class="swiper-slide"
          :class="{ 'seo-banner': index === 0 }"
          v-bind="banner"
          :banner-index="index"
          :title-tag="index === 0 ? titleTag : 'h2'"
        >
          <slot v-if="!useFixedText && index === 0"></slot>
        </BannerCard>
      </div>

      <!-- If we need navigation buttons -->
      <div
        v-if="hasMultipleBanners && !useFixedText"
        :class="`swiper-button-prev btn-prev-${instanceId}`"
      ></div>
      <div
        v-if="hasMultipleBanners && !useFixedText"
        :class="`swiper-button-next btn-next-${instanceId}`"
      ></div>
    </div>
  </BaseModule>
</template>

<script>
import { mapState } from 'vuex';
import Swiper from 'swiper';
import { Autoplay, Navigation } from 'swiper/modules';

import base from '@/mixins/base';
import weightOptions from '@/helpers/weightOptions.json';
import uniqueId from '@/helpers/uniqueId';
import Warning from '@/components/Warning.vue';
import BannerCard from './generic/BannerCard.vue';
import BaseModule from './generic/BaseModule.vue';

const renderTextFields = ({ shouldUseFixedText }) =>
  [
    {
      name: 'topSubtitle',
      type: 'ShortText',
      label: 'Subtítulo',
    },
    {
      name: 'title',
      type: 'ShortText',
      label: 'Título',
      maxlength: 90,
    },
    {
      name: 'titleWeight',
      type: 'VueSelect',
      label: 'Peso do título',
      options: weightOptions,
      default: '300',
    },
    {
      name: 'subtitle',
      type: 'LongText',
      label: 'Texto de apoio',
    },
    {
      name: 'buttonText',
      type: 'ShortText',
      label: 'Texto do botão',
      maxlength: 35,
    },
    {
      name: 'linkUrl',
      type: 'Url',
    },
    {
      name: 'textPosition',
      type: 'VueSelect',
      label: 'Posição do texto',
      default: 'Esquerda',
      options: [
        {
          label: 'Esquerda',
          value: 'Esquerda',
        },
        {
          label: 'Direita',
          value: 'Direita',
        },
        {
          label: 'Centro',
          value: 'Centro',
        },
      ],
    },
  ].map((field) => ({
    ...field,
    isEnabled: ({ useFixedText }) => useFixedText === shouldUseFixedText,
  }));

export default {
  name: 'Banner',
  components: {
    BaseModule,
    BannerCard,
    Warning,
  },
  mixins: [base],
  inheritAttrs: false,
  props: {
    ...BaseModule.props,
    useFixedText: {
      type: Boolean,
      default: false,
    },
    banners: {
      type: Array,
      default() {
        return [];
      },
    },
    useMaxHeight: {
      type: Boolean,
      default: false,
    },
    maxHeight: {
      type: Number,
      default: 350,
    },
    swiperAutoplay: {
      type: Boolean,
      default: false,
    },
    swiperAutoplaySpeed: {
      type: Number,
      default: 10,
    },
    topSubtitle: {
      type: String,
      default: '',
    },
    title: {
      type: String,
      default: '',
    },
    titleWeight: {
      type: String,
      default: '300',
    },
    subtitle: {
      type: String,
      default: '',
    },
    buttonText: {
      type: String,
      default: '',
    },
    linkUrl: {
      type: Object,
      default: () => ({
        type: 'externalLink',
        value: '',
        openInNewTab: true,
      }),
    },
    textPosition: {
      type: String,
      default: 'Esquerda',
    },
  },
  data() {
    return {
      swiper: null,
      isMobile: window.innerWidth < 640,
    };
  },
  computed: {
    bannersWithId() {
      return this.banners.map((banner) => ({ ...banner, id: uniqueId() }));
    },
    activeBanners() {
      if (!this.useFixedText) {
        return this.bannersWithId.filter(this.isBannerActive);
      }

      return this.bannersWithId
        .filter(this.isBannerActive)
        .map(
          ({
            topSubtitle,
            title,
            titleWeight,
            subtitle,
            buttonText,
            linkUrl,
            textPosition,
            ...banner
          }) => banner,
        ); // Remove every text related from banner when using fixed text
    },
    hasMultipleBanners() {
      return this.activeBanners.length > 1;
    },
    // TODO: Improve this v-bind and the whole props system
    fixedBanner() {
      const {
        topSubtitle,
        title,
        titleTag,
        titleWeight,
        subtitle,
        buttonText,
        linkUrl,
        textPosition,
        image,
        imageAltText,
        applyImageFilter,
        imageFilterBlur,
      } = this;

      return {
        topSubtitle,
        title,
        titleTag,
        titleWeight,
        subtitle,
        buttonText,
        linkUrl,
        textPosition,
        background: image ? 'backgroundImage' : 'backgroundColor',
        backgroundColor: !this.activeBanners?.length
          ? this.backgroundColor
          : 'transparent',
        image,
        imageAltText,
        applyImageFilter,
        imageFilterBlur,
      };
    },
    editing() {
      return (
        typeof this.$parent === 'undefined' ||
        typeof this.$parent.$parent === 'undefined'
      );
    },
    swiperAutoplaySettings() {
      if (
        !this.editing &&
        (this.swiperAutoplay || this.useFixedText) &&
        this.swiperAutoplaySpeed
      ) {
        return {
          delay: this.swiperAutoplaySpeed * 1000,
        };
      }
      return false;
    },
    ...mapState(['currentEditableItem', 'activeInstance']),
  },
  watch: {
    hasMultipleBanners(hasMultipleBanners) {
      if (this.swiper) {
        this.swiperDestroy();
      }

      if (hasMultipleBanners) {
        this.swiperInit();
      }
    },
    currentEditableItem() {
      this.slideToActivatedItem();
    },
  },
  mounted() {
    if (this.hasMultipleBanners) {
      this.swiperInit();
    }
    window.addEventListener('resize', () => {
      this.isMobile = window.innerWidth < 640;
    });
  },
  methods: {
    slideToActivatedItem() {
      if (
        this.activeInstance === this.instanceId &&
        this.currentEditableItem != null
      ) {
        if (this.currentEditableItem.includes('banners')) {
          const itemPathIndex = Number(
            this.currentEditableItem.split('.').pop(),
          );

          if (this.swiper) {
            this.swiper.slideTo(itemPathIndex);
          }
        }
      }
    },
    swiperInit() {
      this.$nextTick(() => {
        this.swiper = new Swiper(this.$refs['banners-container'], {
          modules: [Autoplay, Navigation],
          autoplay: this.swiperAutoplaySettings,
          observer: true,
          speed: 500,
          loop: true,
          allowTouchMove: false,
          navigation: {
            nextEl: `.btn-next-${this.instanceId}`,
            prevEl: `.btn-prev-${this.instanceId}`,
          },
        });
        this.slideToActivatedItem();
      });
    },
    swiperDestroy() {
      this.swiper.destroy();
      this.swiper = null;
    },
    extractMinutesFromDate(date) {
      const hours = date.getHours();
      const minutes = date.getMinutes();
      return hours * 60 + minutes;
    },
    isDateInRange(banner) {
      if (!banner.showDisplayConfig) {
        return true;
      }

      const now = new Date();

      const end = banner.displayEndsOn ? new Date(banner.displayEndsOn) : null;

      return end === null || now <= end;
    },
    // Usado apenas no Site
    isTimeInRange(banner) {
      if (!banner.showRecurrence) {
        return true;
      }

      const now = new Date();
      const nowMinutes = this.extractMinutesFromDate(now);
      const startMinutes = banner.startTime
        ? this.extractMinutesFromDate(new Date(banner.startTime))
        : null;
      let endMinutes = banner.endTime
        ? this.extractMinutesFromDate(new Date(banner.endTime))
        : null;

      // tratar casos do tipo "das 18h até às 2 da manhã"
      if (!!endMinutes && !!startMinutes && endMinutes < startMinutes) {
        endMinutes += 24 * 60;
      }

      return (
        (!startMinutes || nowMinutes >= startMinutes) &&
        (!endMinutes || nowMinutes < endMinutes)
      );
    },
    isBannerActive(banner) {
      if (
        this.isMobile &&
        banner.background === 'backgroundVideo' &&
        !banner.imageMobile
      ) {
        return false;
      }

      return this.isDateInRange(banner);
    },
  },
  metadata: {
    extends: BaseModule.metadata,
    tabs: [
      {
        name: 'content',
        groups: [
          {
            name: 'banners',
            label: 'Banners',
            fields: [
              {
                name: 'useFixedText',
                type: 'Checkbox',
                label: 'Usar o mesmo texto para todos os banners',
              },
              ...renderTextFields({ shouldUseFixedText: true }),
              {
                name: 'banners',
                type: 'List',
                minItems: 1,
                itemLabel: 'Banner {index}', // any {index} will be replaced by item's index (1-based)
                addItemLabel: 'Adicionar banner',
                fields: [
                  ...renderTextFields({ shouldUseFixedText: false }),
                  {
                    name: 'showDisplayConfig',
                    type: 'Checkbox',
                    label: 'Definir período de exibição',
                    title: 'Exibição dos banners',
                  },
                  {
                    name: 'displayStartsOn',
                    type: 'DatetimePicker',
                    label: 'Início do período de exibição',
                    enableTime: true,
                    isEnabled() {
                      return this.showDisplayConfig;
                    },
                  },
                  {
                    name: 'displayEndsOn',
                    type: 'DatetimePicker',
                    label: 'Fim do período de exibição',
                    enableTime: true,
                    isEnabled() {
                      return this.showDisplayConfig;
                    },
                  },
                  {
                    name: 'showRecurrence',
                    type: 'Checkbox',
                    label: 'Definir horário de recorrência',
                  },
                  {
                    name: 'startTime',
                    type: 'DatetimePicker',
                    label: 'Início do período de recorrência',
                    mode: 'time',
                    isEnabled() {
                      return this.showRecurrence;
                    },
                  },
                  {
                    name: 'endTime',
                    type: 'DatetimePicker',
                    label: 'Fim do período de recorrência',
                    mode: 'time',
                    isEnabled() {
                      return this.showRecurrence;
                    },
                  },
                  {
                    name: 'background',
                    type: 'VueSelect',
                    label: 'Plano de fundo',
                    default: 'backgroundImage',
                    options: [
                      {
                        label: 'Imagem de fundo',
                        value: 'backgroundImage',
                      },
                      {
                        label: 'Vídeo de fundo',
                        value: 'backgroundVideo',
                      },
                      {
                        label: 'Cor de fundo',
                        value: 'backgroundColor',
                      },
                    ],
                  },
                  {
                    name: 'backgroundColor',
                    type: 'ColorPicker',
                    label: 'Cor de fundo',
                    default: '#000000',
                    isEnabled() {
                      return this.background === 'backgroundColor';
                    },
                  },
                  {
                    name: 'image',
                    type: 'ImageFile',
                    label: 'Imagem de fundo',
                    maxWidth: 1920,
                    maxHeight: 768,
                    helpText: 'Dimensões recomendadas: 1920x768',
                    isEnabled() {
                      return this.background === 'backgroundImage';
                    },
                  },
                  {
                    name: 'imageAltText',
                    type: 'ShortText',
                    label: 'Texto alternativo para imagem de fundo',
                    isEnabled() {
                      return this.background === 'backgroundImage';
                    },
                  },
                  {
                    name: 'differentMobileBackgroundImage',
                    type: 'Checkbox',
                    label: 'Usar uma imagem diferente para mobile',
                    isEnabled() {
                      return this.background === 'backgroundImage';
                    },
                  },
                  {
                    name: 'linkVideo',
                    type: 'Url',
                    label: 'URL do vídeo',
                    availableTypes: ['externalLink'],
                    allowNewTab: false,
                    isEnabled() {
                      return this.background === 'backgroundVideo';
                    },
                  },
                  {
                    name: 'imageMobile',
                    type: 'ImageFile',
                    label: 'Imagem de fundo mobile',
                    maxWidth: 756,
                    maxHeight: 1100,
                    helpText() {
                      const baseText = 'Dimensões recomendadas: 756x1100';

                      if (this.background === 'backgroundVideo') {
                        return `<span class="warning">Vídeos de fundo aparecem somente na versão desktop do site. Adicione uma imagem para o mobile.</span><br><br>${baseText}`;
                      }
                      if (this.background === 'backgroundImage') {
                        return baseText;
                      }
                      return '';
                    },
                    isEnabled() {
                      return (
                        (this.differentMobileBackgroundImage &&
                          this.background === 'backgroundImage') ||
                        this.background === 'backgroundVideo'
                      );
                    },
                  },
                  {
                    name: 'imageMobileAltText',
                    type: 'ShortText',
                    label: 'Texto alternativo para fundo mobile',
                    isEnabled() {
                      return (
                        this.background === 'backgroundVideo' ||
                        (this.differentMobileBackgroundImage &&
                          this.background === 'backgroundImage')
                      );
                    },
                  },
                  {
                    name: 'applyImageFilter',
                    type: 'Checkbox',
                    label: 'Aplicar filtro no fundo',
                    isEnabled() {
                      return this.background !== 'backgroundColor';
                    },
                  },
                  {
                    name: 'imageFilterBlur',
                    type: 'Range',
                    label: 'Esmaecer filtro',
                    helpText:
                      'O filtro pode não aparecer esmaecido em alguns navegadores antigos.',
                    default: 0,
                    min: 0,
                    max: 100,
                    step: 1,
                    format: {
                      suffix: '%',
                    },
                    isEnabled() {
                      return (
                        this.background !== 'backgroundColor' &&
                        this.applyImageFilter
                      );
                    },
                  },
                ],
              },
            ],
          },
          {
            name: 'config',
            label: 'Configuração',
            fields: [
              {
                name: 'useMaxHeight',
                type: 'Checkbox',
                label: 'Configurar uma altura máxima para o banner',
                isEnabled({ banners }) {
                  return !!banners.length;
                },
              },
              {
                name: 'maxHeight',
                type: 'Range',
                label: 'Altura máxima',
                min: 350,
                max: 900,
                step: 10,
                format: {
                  suffix: 'px',
                },
                isEnabled({ useMaxHeight, banners }) {
                  return useMaxHeight && !!banners.length;
                },
              },
              {
                name: 'swiperAutoplay',
                type: 'Checkbox',
                label: 'Habilitar transição automática das imagens (autoplay)',
                isEnabled({ useFixedText }) {
                  return !useFixedText;
                },
              },
              {
                name: 'swiperAutoplaySpeed',
                type: 'Range',
                label: 'Velocidade da transição das imagens',
                min: 1,
                max: 30,
                step: 1,
                format: {
                  suffix: 's',
                },
                isEnabled({ swiperAutoplay, useFixedText }) {
                  return swiperAutoplay || useFixedText;
                },
              },
            ],
          },
        ],
        toRemove: ['text'],
      },
      {
        name: 'appearance',
        toRemove: ['colors', 'padding', 'paddingMobile', 'transitions'],
      },
    ],
  },
};
</script>

<style scoped lang="scss">
@import '@/styles/libs/swiper';

.banner-wrapper {
  position: relative;

  &--proportion {
    height: 145.5vw;

    @include media-breakpoint-up(sm) {
      max-height: var(--max-height);
      height: 40vw;
    }

    &::v-deep {
      > .banner {
        position: absolute;
        width: 100%;
        height: 100% !important;
        top: 0;
        left: 0;
      }
    }

    ::v-deep {
      header {
        .top-subtitle {
          font-size: 0.66em;
          line-height: 1.5em;
        }

        .banner-title {
          font-size: 1em;
          line-height: 1.5em;
        }
      }
    }
  }

  .swiper {
    position: absolute;
    width: 100%;
    height: 100% !important;
    top: 0;
    left: 0;
    z-index: 0;

    .swiper-button-prev,
    .swiper-button-next {
      width: 40px;

      @include media-breakpoint-up(sm) {
        width: 60px;
      }

      &::after {
        background-color: rgba($lb-black, 0.333);
        color: $lb-white;
        border-radius: 50%;
        display: flex;
        justify-content: center;
        align-items: center;
        width: 40px;
        height: 40px;
        font-size: 20px;

        @include media-breakpoint-up(sm) {
          width: 50px;
          height: 50px;
          font-size: 30px;
        }
      }
    }

    .swiper-button-disabled {
      pointer-events: auto;
    }
  }
}
</style>
